import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { IconCash } from '../components/icons';
import { Empty, ErrorNote, FlashNote, Loading, PageHead, Pager, Stat, useFlash } from '../components/ui';
import { api } from '../lib/api';
import { dateTime, day, phone, rupees } from '../lib/format';
import { qk } from '../lib/realtime-cache';
import type { BalancePage, DriverDetail, LedgerPage, MoneyTotals, Page, Payout, Statement, StatementDetail } from '../lib/types';
import { payoutDriverRoute } from '../router';
import { AdjustDialog, CashInDialog, LEDGER_LABEL, PayDialog } from './payoutForms';
import { PayoutRows } from './Payouts';

const signed = (p: number) => `${p > 0 ? '+' : ''}${rupees(p)}`;

function Week({ driverId, s }: { driverId: string; s: Statement }) {
  const [open, setOpen] = useState(s.isCurrent);
  const q = useQuery({
    queryKey: [...qk.payouts, 'statement', driverId, s.weekStart],
    queryFn: () => api.get<StatementDetail>(`/admin/drivers/${driverId}/statements/${s.weekStart}`),
    enabled: open,
  });
  const lines: [string, number][] = [
    ['Opening balance', s.openingPaise],
    [`Earnings (${s.rides} ride${s.rides === 1 ? '' : 's'}, after ${rupees(s.commissionPaise)} commission)`, s.earningsPaise],
    ['Cash kept from riders', -s.cashCollectedPaise],
    ['Tips', s.tipsPaise],
    ['Cancellation fees', s.cancellationFeesPaise],
    ['Milestone bonuses', s.rewardsPaise],
    ['Adjustments', s.adjustmentsPaise],
    ['Paid to the driver', -s.payoutsPaise],
    ['Cash handed in', s.cashHandedInPaise],
  ];
  return (
    <li data-testid={`week-${s.weekStart}`}>
      <button type="button" className="row-link" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="row-main">
          <span className="row-title">
            {day(s.weekStart).replace(/ \d{4}$/, '')} – {day(s.weekEnd)}
            {s.isCurrent && <span className="pill accent">This week</span>}
          </span>
          <span className="row-sub">
            {s.rides} ride{s.rides === 1 ? '' : 's'} · fares {rupees(s.faresPaise)}
          </span>
        </span>
        <span className="row-side">
          <span className="num">{signed(s.closingPaise)}</span>
          <span className="small muted">{s.closingPaise > 0 ? 'Zoomo Rides owes' : s.closingPaise < 0 ? 'Driver owes' : 'Settled'}</span>
        </span>
      </button>
      {open && (
        <div className="row-body stack-sm">
          <dl className="kv small">
            {lines
              .filter(([, v], i) => i === 0 || v !== 0)
              .map(([k, v]) => (
                <div key={k} style={{ display: 'contents' }}>
                  <dt>{k}</dt>
                  <dd className="num">{signed(v)}</dd>
                </div>
              ))}
            <dt className="total">Closing balance</dt>
            <dd className="num total">{signed(s.closingPaise)}</dd>
          </dl>
          {q.isLoading ? (
            <Loading rows={2} />
          ) : q.data?.entries.length ? (
            <details className="history">
              <summary>Every entry this week ({q.data.entries.length})</summary>
              <div className="ledger">
                {q.data.entries.map((e) => (
                  <div key={e.id} className="ledger-line">
                    <div style={{ minWidth: 0 }}>
                      <div>{LEDGER_LABEL[e.type] ?? e.type}</div>
                      <div className="small faint">
                        {dateTime(e.createdAt)}
                        {e.note ? ` · ${e.note}` : ''}
                      </div>
                    </div>
                    <div className="num">{signed(e.amountPaise)}</div>
                  </div>
                ))}
              </div>
            </details>
          ) : null}
        </div>
      )}
    </li>
  );
}

/** One driver's money: balance, payouts, weekly statements and the ledger, with every money action. */
export function PayoutDriverPage() {
  const { driverId } = payoutDriverRoute.useParams();
  const [flash, show] = useFlash(9000);
  const [acting, setActing] = useState<'pay' | 'cash' | 'adjust' | null>(null);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [weekPage, setWeekPage] = useState(1);
  const driver = useQuery({ queryKey: qk.driver(driverId), queryFn: () => api.get<DriverDetail>(`/admin/drivers/${driverId}`) });
  const ledger = useQuery({
    queryKey: [...qk.payouts, 'ledger', driverId, ledgerPage],
    queryFn: () => api.get<LedgerPage>(`/admin/drivers/${driverId}/ledger`, { page: ledgerPage, pageSize: 20 }),
    placeholderData: keepPreviousData,
  });
  // the balance figures (available to pay, pending) come with the ledger; the balances list is the fallback
  const balanceRow = useQuery({
    queryKey: [...qk.payouts, 'balance-of', driverId],
    queryFn: async () => {
      const dd = driver.data!;
      const key = dd.phone?.replace(/\D/g, '').slice(-8) || dd.email || dd.vehicle?.plate || dd.name || undefined;
      const page = await api.get<BalancePage>('/admin/payouts/balances', { search: key, pageSize: 100 });
      return page.items.find((x) => x.driver.id === driverId) ?? null;
    },
    enabled: !!driver.data && ledger.data?.availableToPayPaise == null,
  });
  const weeks = useQuery({
    queryKey: [...qk.payouts, 'statements', driverId, weekPage],
    queryFn: () => api.get<Page<Statement>>(`/admin/drivers/${driverId}/statements`, { page: weekPage, pageSize: 8 }),
    placeholderData: keepPreviousData,
  });
  const payouts = useQuery({
    queryKey: [...qk.payouts, 'list', { driverId }],
    queryFn: () => api.get<Page<Payout>>('/admin/payouts', { driverId, pageSize: 20 }),
  });

  if (driver.isLoading || ledger.isLoading) return <Loading rows={6} />;
  if (driver.error || !driver.data)
    return (
      <div className="stack">
        <PageHead title="Driver money" back={{ to: '/payouts', label: 'Payouts' }} />
        <ErrorNote error={driver.error} />
      </div>
    );
  const d = driver.data;
  const l = ledger.data;
  const balance = l?.balancePaise ?? d.balancePaise;
  const money: MoneyTotals = {
    balancePaise: balance,
    pendingPayoutsPaise: l?.pendingPayoutsPaise ?? balanceRow.data?.pendingPayoutsPaise ?? 0,
    availableToPayPaise: l?.availableToPayPaise ?? balanceRow.data?.availableToPayPaise ?? Math.max(0, balance),
    toCollectPaise: l?.toCollectPaise ?? balanceRow.data?.toCollectPaise ?? Math.max(0, -balance),
  };
  const who = { id: d.id, name: d.name };

  return (
    <div className="stack">
      <PageHead
        back={{ to: '/payouts', label: 'Payouts' }}
        title={d.name ?? 'No name yet'}
        sub={
          <>
            {[d.phone ? phone(d.phone) : null, d.email, d.vehicle?.plateDisplay].filter(Boolean).join(' · ')} ·{' '}
            <Link to="/drivers/$driverId" params={{ driverId: d.id }} className="link">
              Driver page
            </Link>
          </>
        }
        actions={
          <>
            <button className="btn" onClick={() => setActing('adjust')}>
              Adjust
            </button>
            {money.toCollectPaise > 0 && (
              <button className="btn btn-dark" onClick={() => setActing('cash')}>
                Record cash in
              </button>
            )}
            {money.availableToPayPaise > 0 && (
              <button className="btn btn-primary" onClick={() => setActing('pay')}>
                Pay {rupees(money.availableToPayPaise)}
              </button>
            )}
          </>
        }
      />
      <FlashNote flash={flash} testId="money-done" />
      <div className="stats">
        <Stat
          icon={IconCash}
          label="Balance"
          testId="driver-balance"
          value={rupees(Math.abs(balance))}
          foot={balance > 0 ? 'Zoomo Rides owes the driver' : balance < 0 ? 'Driver owes Zoomo Rides' : 'Settled'}
        />
        <Stat label="Can be paid now" value={rupees(money.availableToPayPaise)} foot="Owed, less payouts waiting" />
        <Stat label="Payouts waiting" value={rupees(money.pendingPayoutsPaise)} foot="Created, not marked paid" attn={money.pendingPayoutsPaise > 0} />
        <Stat label="Cash to hand in" value={rupees(money.toCollectPaise)} foot="From cash rides" attn={money.toCollectPaise > 0} />
      </div>

      <section className="card" aria-label="Payouts">
        <div className="card-head">
          <h2>
            Payouts <span className="count-chip">{payouts.data?.total ?? 0}</span>
          </h2>
        </div>
        {payouts.isLoading ? (
          <Loading rows={2} />
        ) : payouts.data?.items.length ? (
          <PayoutRows items={payouts.data.items.map((p) => ({ ...p, driver: p.driver ?? { id: d.id, name: d.name, phone: d.phone } }))} showDriver={false} onDone={(t) => show(t)} />
        ) : (
          <Empty title="No payouts yet" icon={IconCash} />
        )}
      </section>

      <section className="card" aria-label="Weekly statements">
        <div className="card-head">
          <h2>Weekly statements</h2>
          <span className="small muted">Monday to Sunday, India time</span>
        </div>
        {weeks.isLoading ? (
          <Loading rows={3} />
        ) : weeks.error ? (
          <div className="card-body">
            <ErrorNote error={weeks.error} />
          </div>
        ) : weeks.data?.items.length ? (
          <>
            <ul className="rows">
              {weeks.data.items.map((s) => (
                <Week key={s.weekStart} driverId={d.id} s={s} />
              ))}
            </ul>
            <Pager page={weekPage} pageSize={weeks.data.pageSize} total={weeks.data.total} onPage={setWeekPage} />
          </>
        ) : (
          <Empty title="No weeks yet" />
        )}
      </section>

      <section className="card" aria-label="Ledger">
        <div className="card-head">
          <h2>Ledger</h2>
          <span className="small muted">Every entry, newest first</span>
        </div>
        {l?.items.length ? (
          <>
            <div className="card-body ledger" data-testid="ledger">
              {l.items.map((e) => (
                <div key={e.id} className="ledger-line">
                  <div style={{ minWidth: 0 }}>
                    <div>{LEDGER_LABEL[e.type] ?? e.type}</div>
                    <div className="small faint">
                      {dateTime(e.createdAt)}
                      {e.note ? ` · ${e.note}` : ''}
                      {e.rideId && (
                        <>
                          {' · '}
                          <Link to="/rides/$rideId" params={{ rideId: e.rideId }} className="link">
                            ride
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="num" style={{ color: e.amountPaise > 0 ? 'var(--good)' : undefined }}>
                    {signed(e.amountPaise)}
                  </div>
                </div>
              ))}
            </div>
            <Pager page={ledgerPage} pageSize={l.pageSize} total={l.total} onPage={setLedgerPage} />
          </>
        ) : (
          <Empty title="Nothing on the ledger yet" />
        )}
      </section>

      {acting === 'pay' && <PayDialog driver={who} money={money} onClose={() => setActing(null)} onDone={(t) => show(t)} />}
      {acting === 'cash' && <CashInDialog driver={who} max={money.toCollectPaise} onClose={() => setActing(null)} onDone={(t) => show(t)} />}
      {acting === 'adjust' && <AdjustDialog driver={who} onClose={() => setActing(null)} onDone={(t) => show(t)} />}
    </div>
  );
}
